import { Component, ChangeDetectionStrategy, OnInit, inject } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Observable } from "rxjs";
import { withLatestFrom, switchMap, pluck, map } from "rxjs/operators";
import * as Sentry from "@sentry/angular";

import { TasksModel } from "../tasks.model";
import { Task, Shift, I18N } from "../../shared/types";
import { TaskFormResult } from "../types/task-form-result";
import { DynamicFormConfig } from "../../shared/modules/ngx-dynamic-forms/entities";

@Component({
	selector: "create-task-component",
	templateUrl: "create-task.component.html",
	styleUrls: ["create-task.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CreateTaskComponent implements OnInit {
	private readonly tasksModel = inject(TasksModel);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);

	// @ts-expect-error TS2564
	task$: Observable<Task>;
	// @ts-expect-error TS2564
	taskName$: Observable<I18N | string>;
	// @ts-expect-error TS2564
	form$: Observable<DynamicFormConfig>;
	ref: any;
	parent: any;

	goBack() {
		this.router.navigate(["../.."], { relativeTo: this.route });
	}

	async goBackToOverview() {
		const { patient } = this.route.snapshot.params;

		return this.router.navigate([patient, "overview"]);
	}

	onCreate(result: TaskFormResult) {
		const { patient } = this.route.snapshot.params;

		this.tasksModel.createTask(patient, { ...this.ref, executed: true, completed: true, notes: result.notes }, result);
		this.goBackToOverview();
	}

	private taskCreator(patientId: string, shift: Shift, data: any, baseForm: any) {
		const taskData: any = {
			completed: false,
			patientId,
			code: data.code,
			day: shift.shiftDay,
			shift: shift.id,
			name: data.name,
			unplanned: true,
			form: baseForm ? { id: baseForm.id, version: baseForm.version } : undefined,
			hasUserHandlePermission: data.hasUserHandlePermission,
		};

		const id = `${taskData.day}-${taskData.shift}-true-${data.code}`;

		const task = { ...taskData, id };
		this.ref = { ...task };

		return task;
	}

	ngOnInit() {
		const iterator$ = this.route.url.pipe(
			withLatestFrom(this.tasksModel.caretree$),
			map(([url, caretree]) => {
				// Slice `task`/*/*/`create` from url segments
				const codes = url.slice(1, url.length - 1).map((segment) => segment.path);

				let iterator: any = [...caretree];

				for (const code of codes) {
					// @ts-expect-error TS7006
					iterator = iterator.find((tree) => tree.code === code);

					if (!iterator) {
						Sentry.captureMessage(
							`No caretree node found for code [${code}] in iterator with length [${iterator?.length}] and caretree with length [${caretree?.length}]`,
						);
					}

					if (iterator.children) {
						// Keep track of parent to return from shortcut
						this.parent = iterator;
						iterator = iterator.children;
					}
				}

				this.form$ = this.tasksModel.getEvaluationForm(iterator.form);

				return iterator;
			}),
		);

		const baseForm$ = iterator$.pipe(switchMap((iterator) => this.tasksModel.getForm(iterator.form)));

		this.task$ = iterator$.pipe(
			withLatestFrom(this.route.params.pipe(pluck("patient")), this.tasksModel.getCurrentShift(), baseForm$),
			map(([iterator, patient, shift, baseForm]) => this.taskCreator(patient as string, shift, iterator, baseForm)),
		);

		this.taskName$ = this.task$.pipe(map((task) => task.name));
	}
}
